import { useEffect, useRef } from 'react';
import { getPaperById, renderNotice } from './noticeGeneratorEngine';

// The preview is drawn at a fraction of the print size — big enough to stay
// sharp on a retina panel, small enough to redraw on every keystroke.
const PREVIEW_LONG_EDGE = 1100;

/**
 * Live canvas preview of the notice.
 *
 * Rendering is async (web font + SVG rasterisation), so a fast typist can have
 * several renders in flight. Each one draws into its own offscreen canvas and
 * only the newest is blitted to the visible one — otherwise a slow earlier
 * render could land last and show stale text.
 */
export default function NoticeGeneratorPreview({ state }) {
    const canvasRef = useRef(null);
    const runRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const runId = runRef.current + 1;
        runRef.current = runId;
        let cancelled = false;

        const paper = getPaperById(state.paperId);
        const scale = PREVIEW_LONG_EDGE / Math.max(paper.width, paper.height);
        const width = Math.round(paper.width * scale);
        const height = Math.round(paper.height * scale);

        const offscreen = document.createElement('canvas');

        renderNotice(offscreen, state, { width, height })
            .then(() => {
                if (cancelled || runRef.current !== runId) return;
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(offscreen, 0, 0);
            })
            .catch(() => { /* preview simply keeps the last good frame */ });

        return () => { cancelled = true; };
    }, [state]);

    return (
        <div className="flex h-full w-full items-center justify-center overflow-hidden p-1">
            <canvas
                ref={canvasRef}
                className="max-h-full max-w-full rounded-[6px] object-contain shadow-[0_6px_20px_rgba(28,24,21,0.16)]"
            />
        </div>
    );
}
